import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Nav, Container, Button, Dropdown, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FaHome, 
    FaInfoCircle, 
    FaEnvelope, 
    FaUser, 
    FaSignOutAlt, 
    FaSignInAlt, 
    FaUserPlus,
    FaBars,
    FaTimes,
    FaSearch,
    FaBell,
    FaHeart
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../context/AuthContext';
import { ROUTES, MESSAGES } from '../constants/Constants';
import { toast } from 'react-toastify';
import './Header.css';

const Header = () => {
    const { isAuthenticated, logout, user, login } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            toast.success(MESSAGES.SUCCESS.LOGOUT);
        } catch (error) {
            toast.error(MESSAGES.ERROR.GENERAL);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const isActiveRoute = (route) => {
        return location.pathname === route;
    };

    const navItems = [
        { path: ROUTES.HOME, label: 'Home', icon: <FaHome /> },
        { path: ROUTES.ABOUT, label: 'About', icon: <FaInfoCircle /> },
        { path: ROUTES.CONTACT, label: 'Contact', icon: <FaEnvelope /> }
    ];

    return (
        <motion.nav
            className={`header-navbar ${isScrolled ? 'scrolled' : ''}`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Container fluid className="header-container">
                {/* Logo Section */}
                <motion.div
                    className="logo-section"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                >
                    <Link to={ROUTES.HOME} className="logo-link">
                        <div className="logo">
                            <img
                                src="/images/logo.png"
                                alt="Leads Listing Logo"
                                className="logo-image"
                            />
                        </div>
                    </Link>
                </motion.div>

                {/* Search Bar */}
                <motion.div
                    className="search-section"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <form onSubmit={handleSearch} className="search-form">
                        <div className="search-input-group">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search businesses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            <Button type="submit" className="search-button">
                                Search
                            </Button>
                        </div>
                    </form>
                </motion.div>

                {/* Desktop Navigation */}
                <div className="desktop-nav">
                    <Nav className="nav-links">
                        {navItems.map((item, index) => (
                            <motion.div
                                key={item.path}
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                            >
                                <Link
                                    to={item.path}
                                    className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                                >
                                    <span className="nav-icon">{item.icon}</span>
                                    <span className="nav-label">{item.label}</span>
                                </Link>
                            </motion.div>
                        ))}
                    </Nav>

                    {/* User Actions */}
                    <div className="user-actions">
                        {isAuthenticated ? (
                            <>
                                <motion.div
                                    className="notification-bell"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <FaBell />
                                    <Badge bg="danger" className="notification-badge">3</Badge>
                                </motion.div>

                                <motion.div
                                    className="favorites"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <FaHeart />
                                    <Badge bg="danger" className="favorites-badge">5</Badge>
                                </motion.div>

                                <Dropdown className="user-dropdown">
                                    <Dropdown.Toggle as="div" className="user-profile">
                                        <motion.div
                                            className="profile-avatar"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <FaUser />
                                        </motion.div>
                                        <span className="user-name">
                                            {user?.name || 'User'}
                                        </span>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="dropdown-menu-custom">
                                        <Dropdown.Item as={Link} to={ROUTES.DASHBOARD}>
                                            <FaUser className="dropdown-icon" />
                                            Dashboard
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item onClick={handleLogout}>
                                            <FaSignOutAlt className="dropdown-icon" />
                                            Logout
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </>
                        ) : (
                            <div className="auth-buttons">
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Link to={ROUTES.LOGIN} className="auth-button login-btn">
                                        <FaSignInAlt />
                                        <span>Login</span>
                                    </Link>
                                </motion.div>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Link to={ROUTES.SIGNUP} className="auth-button signup-btn">
                                        <FaUserPlus />
                                        <span>Sign Up</span>
                                    </Link>
                                </motion.div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Menu Toggle */}
                <motion.button
                    className="mobile-menu-toggle"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    whileTap={{ scale: 0.9 }}
                >
                    {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                </motion.button>
            </Container>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="mobile-menu-content">
                            <div className="mobile-search">
                                <form onSubmit={handleSearch}>
                                    <div className="search-input-group">
                                        <FaSearch className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search businesses..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="search-input"
                                        />
                                    </div>
                                </form>
                            </div>

                            <div className="mobile-nav-links">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`mobile-nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <span className="nav-icon">{item.icon}</span>
                                        <span className="nav-label">{item.label}</span>
                                    </Link>
                                ))}
                            </div>

                            {isAuthenticated ? (
                                <div className="mobile-user-actions">
                                    <Link to={ROUTES.DASHBOARD} className="mobile-nav-link">
                                        <FaUser />
                                        <span>Dashboard</span>
                                    </Link>
                                    <button onClick={handleLogout} className="mobile-nav-link logout-btn">
                                        <FaSignOutAlt />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="mobile-auth-buttons">
                                    <Link to={ROUTES.LOGIN} className="mobile-auth-button">
                                        <FaSignInAlt />
                                        <span>Login</span>
                                    </Link>
                                    <Link to={ROUTES.SIGNUP} className="mobile-auth-button">
                                        <FaUserPlus />
                                        <span>Sign Up</span>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
};

export default Header;
